import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

import { fetchSunPath } from "../../services/api";
import { buildSunPathSvg, sunPathMonthLabel } from "./sunPathSvg";
import { useIsGreek } from "../../context/LanguageContext";

/**
 * The sun path and the terrain, full size, with the file to keep.
 *
 * The chart is an SVG STRING built by one generator, injected here and handed
 * to the download unchanged. So the picture on screen and the picture in the
 * exported file cannot differ -- and any defect in it exists in a file somebody
 * can open, which is not true of a chart assembled out of React elements.
 *
 * PNG goes through a canvas rather than a second renderer, for the same reason:
 * one drawing, three destinations.
 */
export default function SunPathDialog({ open, onClose, lat, lon, profile, projectName }) {
  const theme = useTheme();
  const greek = useIsGreek();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    // Not Number(): Number(null) and Number("") are both 0, so a project with
    // no coordinates would have quietly drawn the sun path for 0N 0E -- open
    // ocean off Ghana -- and looked like a working chart while doing it.
    const num = (v) =>
      v === null || v === undefined || v === "" ? NaN : Number(v);
    const latN = num(lat);
    const lonN = num(lon);
    if (!Number.isFinite(latN) || !Number.isFinite(lonN)) {
      setError("Set the project's coordinates first.");
      return undefined;
    }
    let cancelled = false;
    setBusy(true);
    setError("");
    (async () => {
      try {
        const out = await fetchSunPath(latN, lonN, profile, null);
        if (!cancelled) setData(out);
      } catch {
        if (!cancelled) setError("The sun path could not be computed for this site.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, lat, lon, profile]);

  const subtitle = useMemo(() => {
    if (!data) return "";
    const ns = data.latitude >= 0 ? (greek ? "Β" : "N") : (greek ? "Ν" : "S");
    const ew = data.longitude >= 0 ? (greek ? "Α" : "E") : (greek ? "Δ" : "W");
    const site = `${Math.abs(data.latitude).toFixed(4)}° ${ns}, ${Math.abs(data.longitude).toFixed(4)}° ${ew}`;
    const terrain = data.horizon?.length > 1
      ? (greek ? `ανάγλυφο έως ${data.horizon_max_deg}°` : `terrain to ${data.horizon_max_deg}°`)
      : (greek ? "δεν έχει ληφθεί προφίλ αναγλύφου" : "no terrain profile fetched");
    return `${site} · ${greek ? "οι ώρες αναφέρονται σε ηλιακό χρόνο" : "hour labels are solar time"} · ${terrain}`;
  }, [data, greek]);

  const svg = useMemo(
    () =>
      data
        ? buildSunPathSvg(data, {
            width: 980,
            height: 560,
            mode: theme.palette.mode,
            greek,
            title: greek ? (projectName ? `Τροχιά ήλιου — ${projectName}` : "Τροχιά ήλιου και ανάγλυφο ορίζοντα") : (projectName ? `Sun path — ${projectName}` : "Sun path and terrain horizon"),
            subtitle,
          })
        : null,
    [data, theme.palette.mode, projectName, subtitle, greek]
  );

  const worst = useMemo(() => {
    const rows = data?.blocked_by_month || [];
    return rows.reduce((a, b) => (a && a.blocked_pct >= b.blocked_pct ? a : b), null);
  }, [data]);

  const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const stem = `sun-path-${(projectName || "site").replace(/[^\w-]+/g, "_")}`;

  const exportSvg = () => {
    if (!svg) return;
    download(new Blob([svg], { type: "image/svg+xml" }), `${stem}.svg`);
  };

  const exportPng = () => {
    if (!svg) return;
    // Through a canvas, from the same string: a second renderer would be a
    // second chance to disagree with what is on screen.
    const image = new Image();
    const source = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
    image.onload = () => {
      const scale = 2;                      // legible when dropped into a report
      const canvas = document.createElement("canvas");
      canvas.width = 980 * scale;
      canvas.height = 560 * scale;
      const ctx = canvas.getContext("2d");
      ctx.scale(scale, scale);
      ctx.drawImage(image, 0, 0);
      canvas.toBlob((blob) => blob && download(blob, `${stem}.png`), "image/png");
    };
    image.src = source;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {greek ? "Τροχιά ήλιου και ανάγλυφο ορίζοντα" : "Sun path and terrain horizon"}
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
          {greek ? "Δείτε ποιες ώρες και ποιους μήνες το γύρω ανάγλυφο κρύβει τον ήλιο, για να ερμηνεύσετε τις απώλειες σκίασης ορίζοντα της αναφοράς." : "Which hours of which months the surrounding terrain actually takes — the question the far-shading loss in the report raises and cannot answer by itself."}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {busy && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {greek ? "Υπολογισμός τροχιάς ήλιου για αυτή τη θέση…" : "Computing the sun's track for this site…"}
            </Typography>
          </Stack>
        )}
        {!busy && error && (
          <Typography variant="body2" sx={{ color: "error.main", py: 2 }}>
            {greek ? (error === "Set the project's coordinates first." ? "Ορίστε πρώτα τις συντεταγμένες του έργου." : "Δεν ήταν δυνατός ο υπολογισμός της τροχιάς ήλιου για αυτή τη θέση.") : error}
          </Typography>
        )}
        {!busy && svg && (
          <>
            <Box
              sx={{ "& svg": { width: "100%", height: "auto", display: "block" } }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
              {greek ? (data.horizon?.length > 1 ? <>
                Η σκιασμένη ζώνη αναπαριστά το ανάγλυφο, με ύψος έως <b>{data.horizon_max_deg}°</b>.
                {worst && <> Η μεγαλύτερη επίδραση εμφανίζεται τον μήνα <b>{sunPathMonthLabel(worst.label, true)}</b>: το <b>{worst.blocked_pct}%</b> των ωρών ημέρας βρίσκεται πίσω από το ανάγλυφο.</>}
                {" "}Οι ώρες αναφέρονται σε ηλιακό χρόνο· το μεσημέρι βρίσκεται στην κεντρική γραμμή ανεξάρτητα από το γεωγραφικό μήκος.
              </> : "Δεν έχει ληφθεί προφίλ αναγλύφου για αυτή τη θέση, οπότε δεν σχεδιάζεται ο ορίζοντας. Αυτό δεν σημαίνει ότι το έδαφος είναι επίπεδο. Λάβετε το προφίλ από την καρτέλα Μοντελοποίηση.") : data.horizon?.length > 1 ? (
                <>
                  The shaded band is the measured terrain, rising to{" "}
                  <b>{data.horizon_max_deg}°</b>
                  {worst && (
                    <>
                      , and it costs <b>{worst.label}</b> the most —{" "}
                      <b>{worst.blocked_pct}%</b> of that month's daylight hours fall
                      behind it
                    </>
                  )}
                  . Hours are solar time, so noon sits on the centre line at every longitude.
                </>
              ) : (
                <>
                  No terrain profile has been fetched for this site, so no skyline is
                  drawn — which is not the same as level ground. Fetch it under Modelling.
                </>
              )}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportSvg} disabled={!svg}>
          SVG
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportPng} disabled={!svg}>
          PNG
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{greek ? "Κλείσιμο" : "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}
